import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { sendAnalyticsEvents } from '../../analytics/common';
import { PLANS } from '../../analytics/eCommerce';
import { EVENTS } from '../../analytics/events';
import RedditPixel from 'react-reddit-pixel';
import ReactGA from 'react-ga4';
import Stripe from './Stripe';
import Braintree from './Braintree';
import { PaymentRequestButtonElement, Elements } from '@stripe/react-stripe-js';
import {
  selectName,
  selectFormValidity,
  selectSuccess,
  sendMainPurchaseRequest,
  selectID,
  braintreePurchase,
  selectIfCardFormShown,
  setIfCardFormShown,
  setCardholderName,
} from './checkoutSlice';
import {
  selectToken,
  selectStripeAccountName,
  selectBtToken,
  selectUserUuid,
  selectPaymentProvider,
} from '../signup/signupSlice';
import {
  selectDefaultPlanId,
  selectLandingType,
  selectPlan,
  selectShowCheckout,
  setPlan,
  setShowCheckout,
} from '../plans/plansSlice';
import {
  selectEventsData,
  selectAnalyticsData,
  setEventDataList,
} from '../events/eventsSlice';
import {
  MediumTitle,
  screenPadding,
  centeredFlex,
  fontSize,
} from '../../mixins';
import UIButton from '../../UI/UIButton';
import BackButton from '../../UI/BackButton';
import CardIcons from './CardIcons';
import Loader from '../../UI/Loader';
import useWindowWidth from '../../helper/useWindowWidth';
import { browserName } from 'react-device-detect';
import braintree from 'braintree-web';
import { Paypal } from './components/Icons';
import { setCheckoutLoader } from '../loader/loaderSlice';
import { setError } from '../error/errorSlice';
import { setAmplitudeUserProperties } from '../../analytics/amplitude';


const isStage = process.env.REACT_APP_ENV === 'stage';
const textAlign = navigator.language.startsWith('ar') ? 'right' : 'left';

const StyledContainer = styled.div`
  ${centeredFlex};
  ${screenPadding};
  justify-content: space-between;
  
   @media screen and (max-width: 430px) {
      height: 100%;
      min-height: unset;
      justify-content: ${props => props.isFB ? 'flex-start' : 'space-between'};
  }
`;

const Title = styled.h1`
  ${MediumTitle};
  line-height: 28px;
  margin: 0;
  margin-bottom: ${props => props.isFB ? '13px' : '0px'};
`;

const PayWith = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  font-weight: 500;
  ${fontSize(16)};
  line-height: 22px;
  text-align: center;
  color: #ffffff;
`;

const PaymentRequestContainer = styled.div`
  width: 90%;
  max-width: 327px;
  
  div iframe {
    border-radius: 24px;
  }
`;

const StyledTerms = styled.p`
  font-weight: 400;
  ${fontSize(12)};
  line-height: 16px;
  text-align: center;
  color: #FFFFFF;
  margin: 32px 0 0;
  
  @media screen and (max-width: 768px){
    margin: ${props => props.isFB ? '12px 0 0' : '32px 0 0'};
  }
`;

const StyledCard = styled.div`
  width: 100%;
  min-height: 225px;
  margin: ${props => (props.windowWidth <= 430 && props.isFB) ? '13px auto 20px' : '60px auto 24px'};
`;

const PaypalButton = styled.div`
  width: 90%;
  max-width: 327px;
  height: 48px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background: #F0C348;
  border-radius: 24px;
  cursor: pointer;
  
  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const AlternativePaymentButtons = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const style = {
  base: {
    color: '#256EB0',
    lineHeight: '24px',
    fontFamily: 'Open Sans, sans-serif',
    fontSize: '18px',
    height: '28px',
    fontWeight: '400',
    textAlign: textAlign,

    '::placeholder': {
      color: '#ADADAD',
      letterSpacing: '1.2px',
      fontSize: '18px',
      lineHeight: '24px',
      fontWeight: '400',
    },
  },
};

const CheckoutScreen = ({ history, stripePromise }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const name = useSelector(selectName);
  const plan = useSelector(selectPlan);
  const token = useSelector(selectToken);
  const stripeAccountName = useSelector(selectStripeAccountName);
  const landingType = useSelector(selectLandingType);
  const isFormValid = useSelector(selectFormValidity);
  const isSuccess = useSelector(selectSuccess);
  const subscriptionId = useSelector(selectID);
  const [planDetails, setPlanDetails] = useState(null);
  const [load, setLoad] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [showPaypal, setShowPaypal] = useState(false);
  const eventsData = useSelector(selectEventsData);
  const stripe = useRef();
  const card = useRef();
  const expiration = useRef();
  const cvc = useRef();
  const cardholder = useRef(null);
  const braintreeInstance = useRef();
  const cardInstance = useRef();
  const threeDSInstance = useRef();
  const analyticsParams = useSelector(selectAnalyticsData);
  const userUuid = useSelector(selectUserUuid);
  const windowWidth = useWindowWidth();
  const isFB = (landingType === 'option_c' || landingType === 'option_b2') && browserName === 'Facebook';
  const paypal = useRef();
  const btToken = useSelector(selectBtToken);
  const showCheckout = useSelector(selectShowCheckout)
  const defaultPlanId = useSelector(selectDefaultPlanId)
  const cardFormVisited = useSelector(selectIfCardFormShown);
  const paymentProvider = useSelector(selectPaymentProvider);
  const useBraintree = paymentProvider === 'braintree';

  const CARD_BRANDS = ['visa', 'mastercard', 'amex', 'discover'];

  const renderBrand = (card) => {
    if (!card || !CARD_BRANDS.includes(card)) return null;
    return (
      <img
        key={card}
        src={`../assets/${card}.png`}
        alt={card}
        width={57}
        height={35}
      />
    );
  };

  useEffect(() => {
    btToken && initializeBraintree(btToken);
  },[btToken]);

  useEffect(() => {
    if (plan) {
      setPlanDetails(PLANS[plan]);
    } 
  }, [plan, dispatch]);

  useEffect(() => {
    if (!useBraintree && stripePromise) {
      stripePromise.then((stripeObj) => {
        stripe.current = stripeObj;
        const elements = stripeObj.elements();

        card.current = elements.create('cardNumber', {
          style: style
        });
        expiration.current = elements.create('cardExpiry', {
          style: style
        });
        cvc.current = elements.create('cardCvc', {
          style: style,
          placeholder: '***',
        });

        setLoad(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBraintree, stripePromise]);

  useLayoutEffect(() => {
    if (!useBraintree && stripe.current && planDetails) {
      setPaymentRequest(null)
        const pr = stripe.current.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: `Family Locator ${planDetails?.description} plan`,
            amount: Math.round(planDetails?.periodPrice * 100),
          },
          displayItems: [
            {
              label: `Family Locator ${planDetails?.description} plan`,
              amount: Math.round(planDetails?.periodPrice * 100),
            },
          ],
          requestPayerName: true,
          requestPayerEmail: true,
        });

        pr.canMakePayment().then((result) => {
          if (result && cardFormVisited && eventsData) {
            setPaymentRequest(pr);
          }
        });

        pr.on('paymentmethod', async (e) => {
          const successPayment = () => e.complete('success');
          const failedPayment = () => e.complete('fail');

          stripePurchaseClick(e, successPayment, failedPayment);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe.current, planDetails, cardFormVisited, useBraintree, eventsData]);

  const initializeBraintree = (token) => {
    braintree.client.create(
      {
        authorization: token
      },
      (clientErr, clientInstance) => {
        if (clientErr) {
          console.error('Error creating client:', clientErr);
          return;
        }

        braintreeInstance.current = clientInstance;

        braintree.paypal.create(
          {
            client: clientInstance
          },
          (paypalErr, paypalInstance) => {
            if (paypalErr) {
              console.error('Error creating PayPal:', paypalErr);
              return;
            }

            paypal.current = paypalInstance;
            setShowPaypal(process.env.REACT_APP_ENABLE_PAYPAL === 'true');
          }
        );
      }
    );
  };

  const onPayPalClick = () => {
    const fullEventsData = {
      ...eventsData,
      payment_method: 'Paypal',
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(setCheckoutLoader({show: true, type: 'checkout'}));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, fullEventsData);

    paypal.current.tokenize({
      flow: 'vault',
      currency: 'USD',
    }, function (tokenizeErr, payload) {
      if (tokenizeErr) {
        if (tokenizeErr.type !== 'CUSTOMER') {
          console.error('Error tokenizing:', tokenizeErr);
        }
        dispatch(setCheckoutLoader({show: false, type: ''}));
        return;
      }

      const localEventData = { payment_method: 'Paypal' };
      const fullEventData = {
          ...eventsData,
          ...localEventData,
      };
      dispatch(setEventDataList(localEventData));
      dispatch(
        braintreePurchase(
          payload.nonce,
          null,
          null,
          plan,
          token,
          analyticsParams,
          fullEventData,
          userUuid,
        )
      );
    });
  };

  const onPurchaseClick = () => {
    if (useBraintree) {
      braintreePurchaseClick()
    } else {
      stripePurchaseClick(null)
    }
  };

  const stripePurchaseClick = (paymentMethod = null, successPayment = null, failedPayment = null) => {
    const paymentMethodName = paymentMethod?.walletName || 'Stripe';
    const paymentCardBrandName = paymentMethod?.card?.brand || eventsData.cardBrand || 'unknown';
    const paymentMethodObj = paymentMethod ? {methodName: paymentMethodName, stripeNonce: paymentMethod.paymentMethod.id} : null;
    const localEventsData = {
      payment_method: paymentMethodName,
      cardBrand: paymentCardBrandName,
      card_brand: paymentCardBrandName,
      stripe_account_name: stripeAccountName,
    };
    const fullEventsData = {
        ...eventsData,
        ...localEventsData,
    };
    dispatch(setEventDataList({ 'Payments gateways': `Stripe [${stripeAccountName}]` }));
    dispatch(setEventDataList(localEventsData));
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(
      sendMainPurchaseRequest(
        paymentMethodObj,
        stripe.current,
        card.current,
        name,
        plan,
        token,
        analyticsParams,
        fullEventsData,
        userUuid,
        successPayment,
        failedPayment,
        )
    );
  };

  const braintreePurchaseClick = () => {
    const cardNumber = card.current.value;
    const cardExpiration = expiration.current.value;
    const cardCVV = cvc.current.value;

    const onError = (error) => {
      dispatch(setCheckoutLoader({ show: false, type: '' }));
      dispatch(setError({ show: true, text: error.message }));
    };

    const fullEventsData = {
      ...eventsData,
      payment_method: 'Braintree',
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, fullEventsData);

    cardInstance.current.tokenize(
      {
        number: cardNumber,
        expirationDate: cardExpiration,
        cvv: cardCVV,
      },
      (error, payload) => {
        if (error) {
          console.error('Payment error', error);
          onError(error);
          return;
        }

        const cardBrand = payload?.details?.cardType || eventsData.cardBrand || 'unknown';
        const localEventsData = {
          payment_method: 'Braintree',
          cardBrand,
          card_brand: cardBrand,
        };
        const extendedEventsData = {
          ...eventsData,
          ...localEventsData,
        };
        setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
        dispatch(setEventDataList(localEventsData));
        dispatch(
          braintreePurchase(
            payload.nonce,
            payload.binData,
            threeDSInstance.current,
            plan,
            token,
            analyticsParams,
            extendedEventsData,
            userUuid,
          )
        );
      }
    );
  };

  const onBackClick = () => {
    sendAnalyticsEvents(EVENTS.subBuyClosed, eventsData);
    dispatch(setShowCheckout(false))
    dispatch(setCardholderName(''))
    dispatch(setPlan(defaultPlanId))
    card.current.clear()
    expiration.current.clear()
    cvc.current.clear()
    cardholder.current.value = ''
  };

  useEffect(() => {
    if (!(plan && planDetails)) return;
    RedditPixel.track('AddToCart', {
      'currency': 'USD',
      'itemCount': 1,
      'price': planDetails.periodPrice,
    });
  },[plan,planDetails]);

  useEffect(() => {
    if (showCheckout) {
      dispatch(setIfCardFormShown(true));
      sendAnalyticsEvents(EVENTS.checkoutScreenShown, eventsData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckout, dispatch]);

  useEffect(() => {
    if (isSuccess) {
      const {periodPrice} = planDetails;
      RedditPixel.track('Purchase', {
        'currency': 'USD',
        'itemCount': 1,
        'transactionId': subscriptionId,
        'price': periodPrice,
        'value': periodPrice,
      });
      if (!isStage) {
        ReactGA.event('purchase', {
          currency: 'USD',
          value: periodPrice,
          transaction_id: subscriptionId,
          gclid: eventsData?.gclid || '',
          items: {
            item_id: plan,
            item_name: plan,
          },
        });
      }
      history.push('/signup/password');
    }
  }, [isSuccess, planDetails, subscriptionId, plan, history, eventsData]);

  if (!stripePromise) {
    return <Loader suspenseShow={true}/>
  }

  return (
    <>
      <StyledContainer isFB={isFB}>
        <BackButton theme={'#fff'} analyticsEvent={onBackClick} id="come-back-button"/>
        <Title isFB={isFB}>{t('checkout')}</Title>
        {windowWidth <= 430 && isFB && <CardIcons windowWidth={windowWidth}/>}
        <StyledCard windowWidth={windowWidth} isFB={isFB}>
          {useBraintree
            ? <Braintree
              btToken={btToken}
              braintreeInstance={braintreeInstance.current}
              cardInstance={cardInstance}
              threeDSInstance={threeDSInstance}
              card={card}
              expiration={expiration}
              cvc={cvc}
              cardholder={cardholder}
              renderBrand={renderBrand}
              planDetails={planDetails}
            />
            : <>
              {load && (
                <Stripe
                  card={card.current}
                  expiration={expiration.current}
                  cvc={cvc.current}
                  inputRef={cardholder}
                />
              )}
            </>
          }
        </StyledCard>
        {(windowWidth > 430 || (windowWidth <= 430 && !isFB))  && <CardIcons />}
        <UIButton disabled={!isFormValid} onClick={() => onPurchaseClick(null)}>{t('confirm_payment')}</UIButton>
        {(paymentRequest || showPaypal) && (
          <AlternativePaymentButtons>
            <PayWith>{t('or_checkout_with')}</PayWith>
            {showPaypal && <PaypalButton id={'paypal-pay-button'} onClick={onPayPalClick}>{Paypal}</PaypalButton>}
            {cardFormVisited && paymentRequest && !useBraintree && (
              <Elements stripe={stripePromise}>
                <PaymentRequestContainer>
                  <PaymentRequestButtonElement
                    options={{
                      paymentRequest,
                      id: 'apple-google-pay-button',
                      style: {
                        paymentRequestButton: {
                          height: '48px',
                          theme: 'light',
                          type: 'default',
                        },
                      },
                    }}
                  />
                </PaymentRequestContainer>
              </Elements>
            )}
          </AlternativePaymentButtons>
        )}
        <StyledTerms isFB={isFB}>{t('billing_terms')}</StyledTerms>
        {isStage && (
          <input
            type={'hidden'}
            id={'test-skip-checkout'}
            onClick={() => history.push('/checkout/success')}
          />
        )}
      </StyledContainer>
    </>
  );
};


export default CheckoutScreen;
